import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { linkPostHogWebIdentity } from '@/lib/analytics/posthog';
import { syncNativeRevenueCatAttribution } from '@/services/nativeBilling';
import {
  captureAcquisitionFromAppsFlyerPayload,
  captureAcquisitionFromUrl,
  captureLandingAcquisitionFromBrowser,
  getAppsFlyerLinkParams,
  getStoredAcquisitionContext,
  resolveAppsFlyerDeepLinkPath,
  resolveNativeDeepLinkPath,
} from '@/utils/acquisitionAttribution';
import {
  addNativeAppsFlyerConversionDataListener,
  addNativeAppsFlyerDeepLinkListener,
  getLatestNativeAppsFlyerConversionData,
  getLatestNativeAppsFlyerDeepLink,
  type AppsFlyerAttributionPayload,
} from '@/utils/nativeAppsFlyerAttribution';
import { isFirstRunTrackingDeferredPath } from '@/utils/firstRunRoutes';
import { detectLlmReferralFromBrowser } from '@/utils/llmReferral';
import { persistUserAcquisitionToServer } from '@/utils/persistUserAcquisition';
import { TRACKING_CONSENT_EVENT, hasTrackingConsent, trackEvent } from '@/utils/trackingManager';
import { isCredentialBearingAuthUrl, sanitizeSensitiveUrl } from '@/utils/urlPrivacy';

const LLM_REFERRAL_SESSION_KEY = 'jp:llm_referral_captured';
const NATIVE_APPSFLYER_ATTRIBUTION_EVENT_KEYS = 'jp:native_appsflyer_attribution_event_keys';
const NATIVE_APPSFLYER_ATTRIBUTION_RETRY_COUNT = 6;
const NATIVE_APPSFLYER_ATTRIBUTION_PRODUCTION_RETRY_DELAY_MS = 2500;
const NATIVE_APPSFLYER_ATTRIBUTION_RETRY_DELAY_MS =
  import.meta.env.MODE === 'test' ? 10 : NATIVE_APPSFLYER_ATTRIBUTION_PRODUCTION_RETRY_DELAY_MS;
// iOS holds AppsFlyer start() until the ATT answer, which can take up to 90s
// of foreground time (AppDelegate), and conversion data arrives a few seconds
// after start(). Polling 110s covers both, so the "missing" diagnostic only
// fires when the data really never came.
export const NATIVE_APPSFLYER_IOS_ATTRIBUTION_WINDOW_MS = 110_000;
const NATIVE_APPSFLYER_IOS_ATTRIBUTION_RETRY_COUNT =
  Math.ceil(NATIVE_APPSFLYER_IOS_ATTRIBUTION_WINDOW_MS / NATIVE_APPSFLYER_ATTRIBUTION_PRODUCTION_RETRY_DELAY_MS) + 1;
const NATIVE_APPSFLYER_PENDING_DEEP_LINK_KEY = 'jp:native_appsflyer_pending_deep_link';
const NATIVE_APPSFLYER_PENDING_DEEP_LINK_TTL_MS = 24 * 60 * 60 * 1000;
const NATIVE_APPSFLYER_ROUTED_DEEP_LINK_KEYS = 'jp:native_appsflyer_routed_deep_link_keys';
type LlmReferralCaptureResult = 'captured' | 'pending_consent' | 'none';

function isIgnoredNativeAttributionUrl(url: string): boolean {
  return url.includes('://auth/callback') || url.includes('/auth/native-callback');
}

/**
 * Fire a one-per-session GA4 + warehouse event when a visitor arrives from an
 * AI assistant (ChatGPT, Perplexity, Claude, etc.). This traffic is invisible
 * in Search Console, so the explicit event is the only reliable way to see it.
 */
function captureLlmReferralOnce(landingPath: string): LlmReferralCaptureResult {
  if (typeof window === 'undefined') return 'none';

  try {
    if (window.sessionStorage.getItem(LLM_REFERRAL_SESSION_KEY)) return 'captured';
  } catch {
    // Ignore storage failures; we still attempt to fire the event below.
  }

  const referral = detectLlmReferralFromBrowser();
  if (!referral) return 'none';

  if (!hasTrackingConsent()) {
    return 'pending_consent';
  }

  void trackEvent('llm_referral', {
    llm_source: referral.source,
    llm_referral_detail: referral.detail,
    llm_referral_via: referral.via,
    landing_path: landingPath,
  });

  try {
    window.sessionStorage.setItem(LLM_REFERRAL_SESSION_KEY, referral.source);
  } catch {
    // Ignore storage failures (private mode / restricted storage).
  }

  return 'captured';
}

function getCurrentLandingPath(): string {
  if (typeof window === 'undefined') return '';
  return sanitizeSensitiveUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`, 'path');
}

function readAppsFlyerPayloadValue(payload: AppsFlyerAttributionPayload, ...keys: string[]): string | null {
  const data = payload.data && typeof payload.data === 'object' ? payload.data : {};

  for (const source of [payload, data]) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    }
  }

  const linkParams = getAppsFlyerLinkParams(payload);
  for (const key of keys) {
    const value = linkParams?.get(key)?.trim();
    if (value) return value;
  }

  return null;
}

function getAppsFlyerPayloadKeys(payload: AppsFlyerAttributionPayload): string[] {
  const keys = new Set<string>();
  const data = payload.data && typeof payload.data === 'object' ? payload.data : {};

  for (const key of Object.keys(payload)) {
    if (key !== 'data') keys.add(key);
  }
  for (const key of Object.keys(data)) {
    keys.add(key);
  }

  return Array.from(keys).sort().slice(0, 60);
}

function buildNativeAppsFlyerAttributionEvent(
  payload: AppsFlyerAttributionPayload,
  source: string,
): Record<string, unknown> {
  return {
    source,
    platform: Capacitor.getPlatform(),
    native_payload_status: readAppsFlyerPayloadValue(payload, 'status'),
    native_payload_error: readAppsFlyerPayloadValue(payload, 'error'),
    af_status: readAppsFlyerPayloadValue(payload, 'af_status'),
    media_source: readAppsFlyerPayloadValue(payload, 'media_source', 'pid', 'af_media_source'),
    campaign: readAppsFlyerPayloadValue(payload, 'campaign', 'c', 'af_campaign'),
    campaign_id: readAppsFlyerPayloadValue(payload, 'campaign_id', 'af_c_id'),
    adset: readAppsFlyerPayloadValue(payload, 'adset', 'af_adset'),
    adset_id: readAppsFlyerPayloadValue(payload, 'adset_id', 'af_adset_id'),
    ad: readAppsFlyerPayloadValue(payload, 'ad', 'af_ad'),
    ad_id: readAppsFlyerPayloadValue(payload, 'ad_id', 'af_ad_id'),
    channel: readAppsFlyerPayloadValue(payload, 'channel', 'af_channel'),
    site_id: readAppsFlyerPayloadValue(payload, 'site_id', 'af_siteid'),
    conversion_type: readAppsFlyerPayloadValue(payload, 'conversion_type'),
    match_type: readAppsFlyerPayloadValue(payload, 'match_type'),
    is_first_launch: readAppsFlyerPayloadValue(payload, 'is_first_launch'),
    click_time: readAppsFlyerPayloadValue(payload, 'click_time', 'af_click_time'),
    install_time: readAppsFlyerPayloadValue(payload, 'install_time'),
    deep_link_value: readAppsFlyerPayloadValue(payload, 'deep_link_value'),
    deep_link_sub1: readAppsFlyerPayloadValue(payload, 'deep_link_sub1'),
    deep_link_sub2: readAppsFlyerPayloadValue(payload, 'deep_link_sub2'),
    deep_link_sub3: readAppsFlyerPayloadValue(payload, 'deep_link_sub3'),
    payload_keys: getAppsFlyerPayloadKeys(payload),
  };
}

function shouldTrackAppsFlyerAttributionEvent(eventData: Record<string, unknown>, userId?: string | null): boolean {
  if (typeof window === 'undefined') return false;

  // The same AppsFlyer payload must be recorded twice when identity changes:
  // first as the anonymous install observation, then again with the canonical
  // auth user after signup. Deduping only by payload permanently orphaned the
  // install from the user because the signed-in effect saw the anonymous key.
  const eventKey = JSON.stringify({
    identity: userId || 'anonymous',
    eventData,
  });

  try {
    const stored = window.sessionStorage.getItem(NATIVE_APPSFLYER_ATTRIBUTION_EVENT_KEYS);
    const keys = stored ? (JSON.parse(stored) as unknown) : [];
    const previousKeys = Array.isArray(keys) ? keys.filter((key): key is string => typeof key === 'string') : [];
    if (previousKeys.includes(eventKey)) return false;

    window.sessionStorage.setItem(
      NATIVE_APPSFLYER_ATTRIBUTION_EVENT_KEYS,
      JSON.stringify([...previousKeys.slice(-9), eventKey]),
    );
  } catch {
    // If sessionStorage is blocked, tracking the diagnostic event once per payload is still safe.
  }

  return true;
}

function hasAppsFlyerConversionSignal(payload: AppsFlyerAttributionPayload | null): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const afStatus = readAppsFlyerPayloadValue(payload, 'af_status');
  if (afStatus && afStatus.trim().toLowerCase() !== 'organic') return true;

  return Boolean(
    readAppsFlyerPayloadValue(
      payload,
      'media_source',
      'pid',
      'af_media_source',
      'campaign',
      'c',
      'af_campaign',
      'deep_link_value',
      'deep_link_sub1',
    ),
  );
}

export function getNativeAppsFlyerAttributionRetryCount(platform: string): number {
  return platform === 'ios'
    ? NATIVE_APPSFLYER_IOS_ATTRIBUTION_RETRY_COUNT
    : NATIVE_APPSFLYER_ATTRIBUTION_RETRY_COUNT;
}

/**
 * A pending deferred deep link waits through first-run routes and through
 * the first session a new member is sent into from onboarding.
 */
function isNativeDeepLinkHoldPath(pathname: string): boolean {
  if (isFirstRunTrackingDeferredPath(pathname)) return true;
  const normalized = (pathname.replace(/\/+$/, '') || '/').toLowerCase();
  return (
    normalized === '/first-session' ||
    // /first-session redirects here, so the first real session is this route.
    normalized === '/daily-prompt/session' ||
    normalized.startsWith('/session/') ||
    /^\/journey\/[^/]+\/(start|learn)$/.test(normalized)
  );
}

function storePendingNativeDeepLink(route: string): void {
  try {
    window.localStorage.setItem(
      NATIVE_APPSFLYER_PENDING_DEEP_LINK_KEY,
      JSON.stringify({ route, storedAt: Date.now() }),
    );
  } catch {
    // Without storage the member simply stays where they are.
  }
}

function takePendingNativeDeepLink(): string | null {
  try {
    const raw = window.localStorage.getItem(NATIVE_APPSFLYER_PENDING_DEEP_LINK_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(NATIVE_APPSFLYER_PENDING_DEEP_LINK_KEY);

    const parsed = JSON.parse(raw) as { route?: unknown; storedAt?: unknown };
    if (typeof parsed.route !== 'string' || !parsed.route.startsWith('/')) return null;
    if (typeof parsed.storedAt !== 'number') return null;
    if (Date.now() - parsed.storedAt > NATIVE_APPSFLYER_PENDING_DEEP_LINK_TTL_MS) return null;
    return parsed.route;
  } catch {
    return null;
  }
}

/**
 * The native plugin keeps returning the latest deep link for the life of the
 * app, and this effect re-reads it on every route change. Each deep link is
 * routed once per app session so a member who moves on is not sent back.
 */
function claimNativeDeepLinkRoute(route: string, payload: AppsFlyerAttributionPayload): boolean {
  let routeKey: string;
  try {
    routeKey = JSON.stringify({ route, payload });
  } catch {
    routeKey = route;
  }

  try {
    const stored = window.sessionStorage.getItem(NATIVE_APPSFLYER_ROUTED_DEEP_LINK_KEYS);
    const keys = stored ? (JSON.parse(stored) as unknown) : [];
    const previousKeys = Array.isArray(keys) ? keys.filter((key): key is string => typeof key === 'string') : [];
    if (previousKeys.includes(routeKey)) return false;

    window.sessionStorage.setItem(
      NATIVE_APPSFLYER_ROUTED_DEEP_LINK_KEYS,
      JSON.stringify([...previousKeys.slice(-9), routeKey]),
    );
  } catch {
    // If sessionStorage is blocked, routing once per effect run is still safe.
  }

  return true;
}

function waitForNativeAppsFlyerRetry(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, NATIVE_APPSFLYER_ATTRIBUTION_RETRY_DELAY_MS);
  });
}

export function AcquisitionCapture() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasTrackedNativeOpenRef = React.useRef(false);
  const hasConsumedNativeLaunchUrlRef = React.useRef(false);
  const isNativeRuntime = React.useMemo(() => Capacitor.isNativePlatform(), []);

  const syncRevenueCatAfterNativeAttributionCapture = React.useCallback(
    (source: string) => {
      if (!user?.id) return;

      void syncNativeRevenueCatAttribution(user.id).catch((error) => {
        console.error(`Error syncing RevenueCat attribution after ${source}:`, error);
      });
    },
    [user?.id],
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isCredentialBearingAuthUrl(window.location.href)) return;
    // Landing capture runs FIRST: it is the only step that records
    // document.referrer, and it early-returns once storage exists. Running
    // the tracking-param capture first used to create storage without a
    // referrer, which is why 100% of user_acquisition rows had referrer null.
    // Skipped on native: the webview's location is capacitor://localhost /
    // https://localhost noise, and native first touch comes from AppsFlyer
    // conversion data or deep links instead.
    if (!isNativeRuntime) {
      captureLandingAcquisitionFromBrowser();
    }
    captureAcquisitionFromUrl(window.location.href, typeof document === 'undefined' ? null : document.referrer);
  }, [isNativeRuntime, location.pathname, location.search, location.hash]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isCredentialBearingAuthUrl(window.location.href)) return;

    const landingPath = getCurrentLandingPath();
    const result = captureLlmReferralOnce(landingPath);
    if (result !== 'pending_consent') return;

    const captureAfterConsent = (event: Event) => {
      const status = event instanceof CustomEvent ? event.detail?.status : null;
      if (status !== 'granted') return;

      if (captureLlmReferralOnce(landingPath) === 'captured') {
        window.removeEventListener(TRACKING_CONSENT_EVENT, captureAfterConsent);
      }
    };

    window.addEventListener(TRACKING_CONSENT_EVENT, captureAfterConsent);

    return () => {
      window.removeEventListener(TRACKING_CONSENT_EVENT, captureAfterConsent);
    };
    // Runs once on mount: document.referrer reflects the external referrer that
    // loaded the document and does not change across SPA route transitions.
  }, []);

  React.useEffect(() => {
    if (!isNativeRuntime) return;

    let active = true;
    let urlListener: { remove: () => Promise<void> } | null = null;

    const routeNativeUrl = (url: string) => {
      const route = resolveNativeDeepLinkPath(url);
      if (!route || !active) return;

      const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (route !== currentRoute) {
        navigate(route);
      }
    };

    void (async () => {
      // getLaunchUrl() keeps returning the cold-start URL for the lifetime of
      // the native app. This effect can restart when React Router replaces a
      // route, so consuming it more than once can bounce a redirect back to
      // its original URL indefinitely (for example /first-session).
      if (!hasConsumedNativeLaunchUrlRef.current) {
        try {
          const launchUrl = await CapacitorApp.getLaunchUrl();
          if (!active || hasConsumedNativeLaunchUrlRef.current) return;
          hasConsumedNativeLaunchUrlRef.current = true;
          if (launchUrl?.url && !isIgnoredNativeAttributionUrl(launchUrl.url)) {
            if (captureAcquisitionFromUrl(launchUrl.url)) {
              syncRevenueCatAfterNativeAttributionCapture('native_launch_url');
            }
            routeNativeUrl(launchUrl.url);
          }
        } catch {
          // Ignore launch URL lookup failures.
        }
      }

      if (active && !hasTrackedNativeOpenRef.current) {
        hasTrackedNativeOpenRef.current = true;
        const acquisition = getStoredAcquisitionContext();
        void trackEvent(
          'app_open',
          {
            platform: Capacitor.getPlatform(),
            acquisition_source: acquisition?.utm_source || acquisition?.network || null,
            acquisition_campaign: acquisition?.utm_campaign || acquisition?.campaign || null,
          },
          { serverOnly: true },
        );
      }

      urlListener = await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        if (!url || isIgnoredNativeAttributionUrl(url)) return;
        if (captureAcquisitionFromUrl(url)) {
          syncRevenueCatAfterNativeAttributionCapture('native_app_url_open');
        }
        routeNativeUrl(url);
      });
    })();

    return () => {
      active = false;
      void urlListener?.remove();
    };
  }, [isNativeRuntime, navigate, syncRevenueCatAfterNativeAttributionCapture]);

  // Opens a deep link held during onboarding once the member reaches a
  // regular screen.
  React.useEffect(() => {
    if (!isNativeRuntime || typeof window === 'undefined') return;
    if (isNativeDeepLinkHoldPath(location.pathname)) return;

    const route = takePendingNativeDeepLink();
    if (!route || route === `${location.pathname}${location.search}${location.hash}`) return;
    navigate(route);
  }, [isNativeRuntime, location.hash, location.pathname, location.search, navigate]);

  // Android: the Play Install Referrer (utm_* from the store link), recorded
  // signed out and again after signup. Loaded lazily to stay out of the
  // startup bundle.
  React.useEffect(() => {
    if (!isNativeRuntime || Capacitor.getPlatform() !== 'android') return;

    let active = true;
    const userId = user?.id;
    void import('@/utils/playInstallReferrer')
      .then(({ trackPlayInstallReferrer }) => {
        if (!active) return;
        return trackPlayInstallReferrer(userId, (eventData) =>
          trackEvent('native_app_attribution', eventData, { serverOnly: true, userId }),
        );
      })
      .catch((error) => {
        console.error('Error reading the Play install referrer:', error);
      });

    return () => {
      active = false;
    };
  }, [isNativeRuntime, user?.id]);

  React.useEffect(() => {
    if (!isNativeRuntime) return;

    let active = true;
    let deepLinkListener: { remove: () => Promise<void> } | null = null;
    let conversionDataListener: { remove: () => Promise<void> } | null = null;
    let sawConversionDataPayload = false;
    let sawConversionDataSignal = false;
    let latestConversionDataPayload: AppsFlyerAttributionPayload | null = null;
    const retryCount = getNativeAppsFlyerAttributionRetryCount(Capacitor.getPlatform());

    const handleAppsFlyerPayload = (
      payload: AppsFlyerAttributionPayload | null,
      shouldRoute: boolean,
      source: string,
    ) => {
      if (!payload || !active) return;
      if (source.includes('conversion_data')) {
        sawConversionDataPayload = true;
        latestConversionDataPayload = payload;
        if (hasAppsFlyerConversionSignal(payload)) {
          sawConversionDataSignal = true;
        }
      }
      const capturedAcquisition = captureAcquisitionFromAppsFlyerPayload(payload);
      if (capturedAcquisition) {
        syncRevenueCatAfterNativeAttributionCapture(source);
        // AppsFlyer data often lands after signup (retry loop runs up to
        // ~12.5s on Android, ~110s on iOS). The server upsert is fill-empty, so a row written early
        // gets enriched instead of staying content-free forever.
        if (user?.id) {
          void persistUserAcquisitionToServer();
        }
      }

      const attributionEvent = buildNativeAppsFlyerAttributionEvent(payload, source);
      if (shouldTrackAppsFlyerAttributionEvent(attributionEvent, user?.id)) {
        void trackEvent('native_app_attribution', attributionEvent, {
          serverOnly: true,
          userId: user?.id,
        });
      }

      // A web store badge puts the visitor's PostHog id in deep_link_sub1
      // (#1353). Linking runs after the events above and never throws; it
      // validates the id and runs once per install.
      const webDistinctId = readAppsFlyerPayloadValue(payload, 'deep_link_sub1');
      if (webDistinctId) {
        linkPostHogWebIdentity(webDistinctId, {
          media_source: readAppsFlyerPayloadValue(payload, 'media_source', 'pid', 'af_media_source'),
          campaign: readAppsFlyerPayloadValue(payload, 'campaign', 'c', 'af_campaign'),
          utm_source: readAppsFlyerPayloadValue(payload, 'utm_source'),
        });
      }

      if (!shouldRoute) return;

      const route = resolveAppsFlyerDeepLinkPath(payload);
      if (!route) return;
      // Claim first: a link that matches the current screen is still used up,
      // so the cached payload never sends the member back here later.
      if (!claimNativeDeepLinkRoute(route, payload)) return;
      if (route === `${location.pathname}${location.search}${location.hash}`) return;

      // A deferred deep link resolves after the ATT answer, which onboarding
      // asks for mid-flow. Hold it until the member is past first run and out
      // of any session or Journey day they are in.
      if (isNativeDeepLinkHoldPath(location.pathname)) {
        storePendingNativeDeepLink(route);
        return;
      }
      navigate(route);
    };

    const trackMissingConversionDataPayload = () => {
      if (!active || sawConversionDataSignal) return;

      const latestPayload = latestConversionDataPayload;
      const latestPayloadKeys = latestPayload ? getAppsFlyerPayloadKeys(latestPayload) : [];
      const source = sawConversionDataPayload
        ? 'conversion_data_no_attribution_signal'
        : 'latest_conversion_data_missing';

      const eventData = {
        source,
        platform: Capacitor.getPlatform(),
        status: sawConversionDataPayload ? 'no_attribution_signal_after_retry' : 'missing_after_retry',
        native_payload_status: latestPayload ? readAppsFlyerPayloadValue(latestPayload, 'status') : null,
        native_payload_error: latestPayload ? readAppsFlyerPayloadValue(latestPayload, 'error') : null,
        payload_keys: latestPayloadKeys,
        retry_count: retryCount,
        retry_window_ms: (retryCount - 1) * NATIVE_APPSFLYER_ATTRIBUTION_RETRY_DELAY_MS,
      };
      if (shouldTrackAppsFlyerAttributionEvent(eventData, user?.id)) {
        void trackEvent('native_app_attribution', eventData, {
          serverOnly: true,
          userId: user?.id,
        });
      }
    };

    void (async () => {
      try {
        deepLinkListener = await addNativeAppsFlyerDeepLinkListener((payload) => {
          handleAppsFlyerPayload(payload, true, 'deep_link_listener');
        });
        conversionDataListener = await addNativeAppsFlyerConversionDataListener((payload) => {
          handleAppsFlyerPayload(payload, true, 'conversion_data_listener');
        });
      } catch (error) {
        console.error('Error registering AppsFlyer attribution listeners:', error);
      }

      for (let attempt = 0; active && attempt < retryCount; attempt += 1) {
        // The longer iOS window only waits for conversion data that has not
        // come yet; the listeners catch any deep link that arrives later.
        const isExtendedAttempt = attempt >= NATIVE_APPSFLYER_ATTRIBUTION_RETRY_COUNT;
        if (isExtendedAttempt && sawConversionDataPayload) break;
        if (attempt > 0) {
          await waitForNativeAppsFlyerRetry();
          if (!active) break;
        }

        try {
          if (!isExtendedAttempt) {
            handleAppsFlyerPayload(await getLatestNativeAppsFlyerDeepLink(), true, 'latest_deep_link');
          }
          handleAppsFlyerPayload(await getLatestNativeAppsFlyerConversionData(), true, 'latest_conversion_data');
        } catch (error) {
          console.error('Error reading latest AppsFlyer attribution payload:', error);
        }
      }
      trackMissingConversionDataPayload();
    })();

    return () => {
      active = false;
      void deepLinkListener?.remove();
      void conversionDataListener?.remove();
    };
  }, [
    isNativeRuntime,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    syncRevenueCatAfterNativeAttributionCapture,
    user?.id,
  ]);

  return null;
}

export default AcquisitionCapture;
